'use client';

import type { IconName } from '@polyseme/design-system';

import type { RiskProfile } from '~/landing/core/simulator/future-savings-planner/constants';
import type { FormSchema } from '~/landing/core/simulator/future-savings-planner/schema';

import {
  FieldWrapper,
  FormField,
  Icon,
  OptionSelector,
  ProjectionChart,
  RadioGroup,
  Select,
  Separator,
  SimpleCard,
  TextInput,
  Typography,
} from '@polyseme/design-system';
import {
  formatCurrency,
  formatPercent,
} from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  getAllProjections,
  getMaxAmount,
  getRateByRiskProfile,
} from '~/landing/core/simulator/future-savings-planner/calculations';
import {
  futureSavingsPlannerConstants,
  riskProfiles,
} from '~/landing/core/simulator/future-savings-planner/constants';
import { useRetirementAgeOptions } from '~/landing/utils';

import { RangeSliderInput } from './ranger-slider-input';

const riskProfileIconMap = {
  CAUTION: 'shieldban',
  BALANCED: 'scale',
  DYNAMISM: 'trending-up',
  BOLDNESS: 'target',
} satisfies Record<RiskProfile, IconName>;

const COLORS = {
  contribution: {
    dotBg: 'bg-brand-purple',
    textColor: 'text-brand-purple',
    lineColor: '#B45FC0',
  },
  yield: {
    dotBg: 'bg-brand-secondary',
    textColor: 'text-brand-secondary',
    lineColor: '#DBFAEC',
  },
  projection: {
    dotBg: 'bg-brand',
    textColor: 'text-brand',
    lineColor: '#006F73',
  },
};

type Props = {
  blurAmounts?: boolean;
};

const VARIANT = 'retirement';

export const FutureSavingsPlanner = ({ blurAmounts = false }: Props) => {
  const form = useFormContext<FormSchema>();
  const t = useTranslations('landing');

  const [
    initialDeposit,
    monthlyPayment,
    birthDate,
    retirementAge,
    riskProfile,
  ] = form.watch([
    'futureSavingsPlanner.initialDeposit',
    'futureSavingsPlanner.monthlyPayment',
    'futureSavingsPlanner.birthDate',
    'futureSavingsPlanner.retirementAge',
    'futureSavingsPlanner.riskProfile',
  ]);
  const allProjections = useMemo(
    () =>
      getAllProjections({
        birthDate,
        initialDeposit,
        monthlyPayment,
        retirementAge,
      }),
    [birthDate, initialDeposit, monthlyPayment, retirementAge]
  );

  const maxAmount = useMemo(
    () => getMaxAmount(allProjections),
    [allProjections]
  );

  const retirementOptions = useRetirementAgeOptions({
    dateOfBirth: birthDate,
    isRetired: false,
  });

  const rate = getRateByRiskProfile(riskProfile);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <SimpleCard
        title={t('retraite.futureSavingsPlanner.form.projection.title')}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="futureSavingsPlanner.initialDeposit"
            render={({ field }) => (
              <RangeSliderInput
                className="xl:col-span-full"
                {...field}
                helper={t(
                  'retraite.futureSavingsPlanner.form.projection.initialDeposit.helper',
                  {
                    min: formatCurrency(
                      futureSavingsPlannerConstants.initialDeposit.min
                    ),
                    max: formatCurrency(
                      futureSavingsPlannerConstants.initialDeposit.max
                    ),
                  }
                )}
                label={t(
                  'retraite.futureSavingsPlanner.form.projection.initialDeposit.label'
                )}
                max={futureSavingsPlannerConstants.initialDeposit.max}
                min={futureSavingsPlannerConstants.initialDeposit.min}
                step={futureSavingsPlannerConstants.initialDeposit.step}
              />
            )}
          />
          <FormField
            control={form.control}
            name="futureSavingsPlanner.monthlyPayment"
            render={({ field }) => (
              <RangeSliderInput
                className="xl:col-span-full"
                {...field}
                helper={t(
                  'retraite.futureSavingsPlanner.form.projection.monthlyPayment.helper',
                  {
                    min: formatCurrency(
                      futureSavingsPlannerConstants.monthlyPayment.min
                    ),
                    max: formatCurrency(
                      futureSavingsPlannerConstants.monthlyPayment.max
                    ),
                  }
                )}
                label={t(
                  'retraite.futureSavingsPlanner.form.projection.monthlyPayment.label'
                )}
                max={futureSavingsPlannerConstants.monthlyPayment.max}
                min={futureSavingsPlannerConstants.monthlyPayment.min}
                step={futureSavingsPlannerConstants.monthlyPayment.step}
              />
            )}
          />

          <FormField
            control={form.control}
            name="futureSavingsPlanner.birthDate"
            render={({ field }) => (
              <FieldWrapper
                label={t(
                  'retraite.futureSavingsPlanner.form.projection.birthDate'
                )}
              >
                <TextInput
                  className="appearance-none md:appearance-auto"
                  {...field}
                  type="date"
                  onChange={(event) => {
                    field.onChange(event);
                    form.resetField('futureSavingsPlanner.retirementAge');
                  }}
                />
              </FieldWrapper>
            )}
          />
          <FormField
            control={form.control}
            name="futureSavingsPlanner.retirementAge"
            render={({ field }) => (
              <FieldWrapper
                label={t(
                  'retraite.futureSavingsPlanner.form.projection.retirementAge'
                )}
              >
                <Select
                  aria-label={t(
                    'retraite.futureSavingsPlanner.form.projection.retirementAge'
                  )}
                  {...field}
                  disabled={retirementOptions.length === 0}
                  options={retirementOptions}
                  value={field.value ? String(field.value) : undefined}
                />
              </FieldWrapper>
            )}
          />
        </div>
        <Separator className="my-6" />
        <Typography className="mb-6" variant="title-4">
          {t('retraite.futureSavingsPlanner.form.profile.title')}
        </Typography>
        <SimpleCard>
          <FormField
            control={form.control}
            name="futureSavingsPlanner.riskProfile"
            render={({ field }) => (
              <RadioGroup
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                value={field.value}
                onValueChange={field.onChange}
              >
                {riskProfiles.map((profile) => (
                  <OptionSelector
                    key={profile}
                    as="radio"
                    label={t(`retraite.riskProfile.${profile}.name`)}
                    value={profile}
                  />
                ))}
              </RadioGroup>
            )}
          />

          <Separator className="my-6" />
          <div className="mb-2 flex items-center gap-2">
            <Icon className="size-6" name={riskProfileIconMap[riskProfile]} />
            <Typography variant="title-6">
              {t(
                'retraite.futureSavingsPlanner.form.profile.information.title',
                {
                  riskProfile: t(`retraite.riskProfile.${riskProfile}.name`),
                }
              )}
            </Typography>
          </div>
          <Typography variant="body">
            {t(`retraite.riskProfile.${riskProfile}.description`)}
          </Typography>
        </SimpleCard>
      </SimpleCard>

      <SimpleCard
        className="overflow-x-auto"
        title={t('retraite.futureSavingsPlanner.result.title')}
      >
        <ProjectionChart
          blurAmounts={blurAmounts}
          chartConfig={{
            optimistic: {
              color: COLORS.yield.lineColor,
            },
            median: {
              color: COLORS.projection.lineColor,
            },
            pessimistic: {
              color: COLORS.yield.lineColor,
            },
            investmentMade: {
              color: COLORS.contribution.lineColor,
            },
          }}
          config={{
            contribution: {
              colors: COLORS.contribution,
              legend: t(
                `retraite.futureSavingsPlanner.result.caption.contribution.${VARIANT}`
              ),
              explanation: {
                subtitle: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.contribution.label'
                ),
                sentence: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.contribution.content'
                ),
              },
            },
            yield: {
              colors: COLORS.yield,
              legend: t(
                `retraite.futureSavingsPlanner.result.caption.yield.${VARIANT}`
              ),
              explanation: {
                subtitle: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.yield.label'
                ),
                sentence: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.yield.content'
                ),
              },
            },
            projection: {
              colors: COLORS.projection,
              legend: t(
                `retraite.futureSavingsPlanner.result.caption.projection.${VARIANT}`
              ),
              explanation: {
                subtitle: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.projection.label'
                ),
                sentence: t(
                  'retraite.futureSavingsPlanner.result.explanation.GARANCE.projection.content',
                  {
                    rate: formatPercent(rate.median),
                  }
                ),
              },
            },
          }}
          explanationsTitle={t(
            'retraite.futureSavingsPlanner.result.explanation.GARANCE.label'
          )}
          maxAmount={maxAmount}
          projections={allProjections[riskProfile]}
          tooltipLabels={{
            investmentMade: t(
              `retraite.futureSavingsPlanner.result.tooltip.investmentMade.${VARIANT}`
            ),
            interestGenerated: t(
              'retraite.futureSavingsPlanner.result.tooltip.interestGenerated'
            ),
            totalSavings: t(
              `retraite.futureSavingsPlanner.result.tooltip.savings.atYear`
            ),
            totalSavingsAtLastYear: t(
              `retraite.futureSavingsPlanner.result.tooltip.savings.atLastYear.${VARIANT}`
            ),
          }}
        />
      </SimpleCard>
    </div>
  );
};
